import { useStore } from "zustand";
import type { NewsFeedTermsType } from "@/data-access/news-feeds/queries/news-feeds-queries";
import { derive } from "@/lib/derive-zustand";
import useNewsFeedsStore from "./news-feeds.slice";

interface FeedMetadata {
  name: string;
  terms: NewsFeedTermsType[];
  tags: string[];
}

const deriveFeedMetadata = derive<FeedMetadata>((get) => {
  const { allNewsFeeds, editingFeedId } = get(useNewsFeedsStore);

  // If no editing feed ID, return default values
  if (!editingFeedId) {
    return { name: "", terms: [], tags: [] };
  }

  // Find the specific feed being edited
  const feed = allNewsFeeds.find((f) => f.id === editingFeedId);

  if (!feed) {
    return { name: "", terms: [], tags: [] };
  }

  return {
    name: feed.name,
    terms: feed.terms ?? [],
    tags: feed.knownTags?.map((t: { tag: string }) => t.tag) ?? [],
  };
});

const deriveFeedOptions = derive<Array<{ feedId: string; name: string }>>(
  (get) => {
    const { allNewsFeeds } = get(useNewsFeedsStore);

    return allNewsFeeds.map((feed) => ({
      feedId: feed.id,
      name: feed.name,
    }));
  },
);

// Hooks
const useFeedMetadata = () => useStore(deriveFeedMetadata);

export const useFeedOptions = () => useStore(deriveFeedOptions);

/**
 * Hook that gets feed name with fallback to URL params
 * Use this when you have access to feedId from useParams
 */
const useFeedName = (feedId?: string | null) => {
  const { allNewsFeeds, currentFeedId } = useNewsFeedsStore();

  // Use currentFeedId from store first, then fallback to provided feedId
  const activeFeedId = currentFeedId || feedId || null;

  if (!activeFeedId) {
    return "";
  }

  const feed = allNewsFeeds.find((f) => f.id === activeFeedId);
  return feed?.name ?? "";
};

// Exports
export { useFeedMetadata, useFeedName };
